"use client";

import { useEffect, useRef, useState } from "react";

export interface StoryNavItem {
  id: string;
  label: string;
}

/**
 * Scroll-spy: returns the id of the section currently being read. The first
 * section stays active until the next one crosses the `offset` line (a little
 * below the fixed nav), so it's active from the top of the article. Shared by
 * the desktop sidebar nav and the mobile scroll bar.
 */
export function useActiveSection(items: StoryNavItem[], offset = 160) {
  const [activeId, setActiveId] = useState(items[0]?.id ?? "");
  const frame = useRef<number | null>(null);

  useEffect(() => {
    const measure = () => {
      frame.current = null;
      let current = items[0]?.id ?? "";
      for (const item of items) {
        const el = document.getElementById(item.id);
        if (!el) continue;
        if (el.getBoundingClientRect().top - offset <= 0) {
          current = item.id;
        }
      }
      setActiveId(current);
    };

    const onScroll = () => {
      if (frame.current != null) return;
      frame.current = window.requestAnimationFrame(measure);
    };

    measure();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (frame.current != null) window.cancelAnimationFrame(frame.current);
    };
  }, [items, offset]);

  return activeId;
}

/**
 * Reading progress (0–1) through the active section, used to fill the nav's
 * "Scroll Indicator" gauge as you scroll. 0 when the section's top reaches the
 * `offset` line, 1 once its bottom passes it.
 */
export function useSectionProgress(activeId: string, offset = 160) {
  const [progress, setProgress] = useState(0);
  const frame = useRef<number | null>(null);

  useEffect(() => {
    const measure = () => {
      frame.current = null;
      const el = activeId ? document.getElementById(activeId) : null;
      if (!el) {
        setProgress(0);
        return;
      }
      const rect = el.getBoundingClientRect();
      const ratio = rect.height > 0 ? (offset - rect.top) / rect.height : 0;
      setProgress(Math.min(1, Math.max(0, ratio)));
    };

    const onScroll = () => {
      if (frame.current != null) return;
      frame.current = window.requestAnimationFrame(measure);
    };

    measure();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (frame.current != null) window.cancelAnimationFrame(frame.current);
    };
  }, [activeId, offset]);

  return progress;
}
